// Login-time MFA challenge page (Part 2 §13.6).
//
// `/login/mfa` is the destination when `auth.login` returns a challenge
// rather than a session. The challengeToken arrives via Router state
// (set by Login.tsx after submitting credentials). On a successful TOTP
// verify, the auth context is hydrated and we route to '/'.
//
// "Use a recovery code instead" → /login/recovery (consumes a one-time
// code; auth-service marks the consumed code as used and flags the user
// for re-enrollment).

import { useState, useEffect, type FormEvent } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { trpc } from '../api/trpc';
import { useAuth } from '../hooks/useAuth';
import { CadeAvatar } from '../components/CadeAvatar';

interface LocationState { challengeToken?: string }

export function LoginMfaPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hydrate } = useAuth();
  const state = (location.state as LocationState | null) ?? {};
  const challengeToken = state.challengeToken;

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const complete = trpc.auth.completeLoginMfaChallenge.useMutation();

  useEffect(() => {
    if (!challengeToken) {
      // Direct navigation to /login/mfa without a challenge — punt back.
      navigate('/login', { replace: true });
    }
  }, [challengeToken, navigate]);

  if (!challengeToken) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const clean = code.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(clean)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    try {
      const result = await complete.mutateAsync({
        challengeToken: challengeToken!,
        method: 'totp',
        code: clean,
      });
      hydrate({ token: result.token, userId: result.userId, tenantId: result.tenantId });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    }
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <CadeAvatar size={80} />
          <span className="login-logo-text">Verify it's you</span>
        </div>
        <form className="login-form" onSubmit={(e) => { void handleSubmit(e); }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted, #888)' }}>
            Enter the 6-digit code from your authenticator app to finish signing in.
          </p>
          <div className="form-field">
            <label htmlFor="login-mfa-code">Code</label>
            <input
              id="login-mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              autoComplete="one-time-code"
              placeholder="123456"
            />
          </div>
          {error && <p className="login-error" data-testid="login-mfa-error">{error}</p>}
          <button
            type="submit"
            disabled={complete.isPending}
            className="btn-recommended"
            data-testid="login-mfa-submit"
          >
            {complete.isPending
              ? <><span className="btn-spinner" aria-hidden="true" data-testid="login-mfa-spinner" />Verifying…</>
              : 'Verify and sign in'}
          </button>
        </form>
        <p style={{ marginTop: 16, fontSize: 13, textAlign: 'center' }}>
          <Link to="/login/recovery" state={{ challengeToken }}>
            Use a recovery code instead
          </Link>
        </p>
      </div>
    </div>
  );
}
